import React from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Download, AlertTriangle, TrendingUp, TrendingDown, Target } from 'lucide-react';
import { getRole } from '../data/framework';
import { personById } from '../data/demoOrganisation';
import { DOMAIN_SCORES, SCORING_CYCLE, SCORING_ROLE_ID, SCORING_SUBJECT_ID } from '../data/scoring';
type Row = {
  domain: string;
  short: string;
  self: number;
  others: number;
  board: number;
  peers: number;
  directs: number;
  characteristic: number;
  behaviour: number;
};
const role = getRole(SCORING_ROLE_ID);
const subject = personById(SCORING_SUBJECT_ID);
const round = (n: number) => Math.round(n * 10) / 10;
const ROWS: Row[] = (role?.domains ?? []).map((d, i) => {
  const s = DOMAIN_SCORES[i % DOMAIN_SCORES.length];
  return {
    domain: d.name,
    short: shorten(d.name),
    self: s.self,
    others: round((s.board + s.peers + s.directs) / 3),
    board: s.board,
    peers: s.peers,
    directs: s.directs,
    characteristic: s.characteristic,
    behaviour: s.behaviour
  };
});
const overall = round(ROWS.reduce((n, r) => n + r.others, 0) / (ROWS.length || 1));
const sorted = [...ROWS].sort((a, b) => b.others - a.others);
const highest = sorted[0];
const lowest = sorted[sorted.length - 1];
const blindSpots = ROWS.filter(r => r.self - r.others >= 0.8);
const avgCharacteristic = round(ROWS.reduce((n, r) => n + r.characteristic, 0) / (ROWS.length || 1));
const avgBehaviour = round(ROWS.reduce((n, r) => n + r.behaviour, 0) / (ROWS.length || 1));
function shorten(name: string): string {
  const first = name.split(/,| & | and /)[0].trim();
  return first.length > 22 ? `${first.slice(0, 20)}…` : first;
}
const getHeatmapColor = (score: number) => {
  if (score >= 4.5) return 'bg-emerald-500 text-white';
  if (score >= 4.0) return 'bg-emerald-400 text-white';
  if (score >= 3.5) return 'bg-emerald-300 text-slate-800';
  if (score >= 3.0) return 'bg-amber-300 text-slate-800';
  if (score >= 2.5) return 'bg-amber-400 text-slate-900';
  return 'bg-rose-500 text-white';
};
export function ScoringDashboard() {
  if (!role || ROWS.length === 0) {
    return <div className="py-24 text-center text-sm text-slate-500" data-id="element-2213" data-mp="1z9unmw20ufnm">
        No scoring data available.
      </div>;
  }
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-2214" data-mp="1l9sb7pchgefr">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-2215" data-mp="1qa7nedl13fw5">
        <div data-id="element-2216" data-mp="1bcnnhl2zoawu">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-2217" data-mp="1bd7510oq8xac">
            Scoring Dashboard
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2218" data-mp="1gcjorckaxo8p">
            {subject.name}, {role.name} · {SCORING_CYCLE}
          </p>
        </div>
        <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center bg-white self-start sm:self-auto" data-id="element-2219" data-mp="35r84nns3pobh">
          <Download className="h-4 w-4 mr-2" data-id="element-2220" data-mp="z2frybmtcjwm" />
          Export PDF
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2221" data-mp="u4wg1idl8j7u">
        <StatCard label="Overall Score" icon={<Target className="h-4 w-4 text-indigo-500" data-id="element-2223" data-mp="141izc2q9n27r" />} data-id="element-2222" data-mp="18sr9x23c0tmi">
          <div className="flex items-baseline space-x-2" data-id="element-2224" data-mp="13zy1a34qt155">
            <span className="text-3xl font-bold text-slate-900" data-id="element-2225" data-mp="1hrw3rv3y7ysc">
              {overall.toFixed(1)}
            </span>
            <span className="text-sm text-slate-500" data-id="element-2226" data-mp="1dqapc5brncg1">/ 5.0</span>
          </div>
          <div className="mt-2 text-xs text-slate-500" data-id="element-2227" data-mp="1rr3gqh6rtrex">
            Across {ROWS.length} competency domains
          </div>
        </StatCard>
        <StatCard label="Strongest Domain" icon={<TrendingUp className="h-4 w-4 text-emerald-500" data-id="element-2229" data-mp="3tj9ldn9a5ojm" />} data-id="element-2228" data-mp="1mpnwusgbxqy4">
          <div className="text-base font-semibold text-slate-900 leading-snug" data-id="element-2230" data-mp="3ojdgzmwo7v3j">
            {highest.domain}
          </div>
          <div className="mt-1 text-sm font-medium text-emerald-600" data-id="element-2231" data-mp="3pz94ywnzpjp2">
            {highest.others.toFixed(1)} / 5.0
          </div>
        </StatCard>
        <StatCard label="Development Domain" icon={<TrendingDown className="h-4 w-4 text-rose-500" data-id="element-2233" data-mp="3udut97u9jo1" />} data-id="element-2232" data-mp="3v16uu9qwbcqz">
          <div className="text-base font-semibold text-slate-900 leading-snug" data-id="element-2234" data-mp="8sp2bdanvi33">
            {lowest.domain}
          </div>
          <div className="mt-1 text-sm font-medium text-rose-600" data-id="element-2235" data-mp="37b44pz6jkgv0">
            {lowest.others.toFixed(1)} / 5.0
          </div>
        </StatCard>
        <StatCard label="Blind Spots" icon={<AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-2237" data-mp="3h778takyepkl" />} data-id="element-2236" data-mp="3h7z38i2teh9a">
          <div className="flex items-baseline space-x-2" data-id="element-2238" data-mp="3lyxlhn7d5pf6">
            <span className="text-3xl font-bold text-slate-900" data-id="element-2239" data-mp="3m0igfdxlbadk">
              {blindSpots.length}
            </span>
            <span className="text-sm text-slate-500" data-id="element-2240" data-mp="2tiefllvpuvto">domains</span>
          </div>
          <div className="mt-2 text-xs text-amber-600 font-medium" data-id="element-2241" data-mp="2tg1qmorjjl74">
            Self-rating 0.8+ above others
          </div>
        </StatCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2242" data-mp="16mq1iqo7hygp">
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-2243" data-mp="11mbtv9qioqj7">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-2244" data-mp="wnheyivo7m4d">Self vs Others</h2>
          <p className="text-sm text-slate-500 mb-4" data-id="element-2245" data-mp="wplekomjdyyu">
            Average score by competency domain
          </p>
          <div className="h-[360px] w-full" data-id="element-2246" data-mp="mxpfpbcmm4tk">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2247" data-mp="mwmo117hbycl">
              <RadarChart cx="50%" cy="50%" outerRadius="65%" data={ROWS} data-id="element-2248" data-mp="ivufybacfzhw">
                <PolarGrid stroke="#e2e8f0" data-id="element-2249" data-mp="itzqqks1hl80" />
                <PolarAngleAxis dataKey="short" tick={{
                fill: '#64748b',
                fontSize: 10
              }} data-id="element-2250" data-mp="dxsob1yuqrut" />
                <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} data-id="element-2251" data-mp="900v84mh0zp1" />
                <Radar name="Self" dataKey="self" stroke="#818cf8" fill="#818cf8" fillOpacity={0.3} data-id="element-2252" data-mp="3qfu1xphswuqv" />
                <Radar name="Others (avg)" dataKey="others" stroke="#34d399" fill="#34d399" fillOpacity={0.3} data-id="element-2253" data-mp="3hhi0esfeccjn" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                paddingTop: '12px'
              }} data-id="element-2254" data-mp="d3s9050nq46c" />
                <Tooltip data-id="element-2255" data-mp="i0ac70gc9ugk" />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-2256" data-mp="47aviv4tlu7f">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-2257" data-mp="895ye42egfnb">
            Characteristic vs Behaviour
          </h2>
          <p className="text-sm text-slate-500 mb-4" data-id="element-2258" data-mp="vyqemf764ysw">
            Others' ratings split by question type
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-2259" data-mp="2lhfaw494gozh">
            <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3" data-id="element-2260" data-mp="2lfvt297ti5vl">
              <div className="text-xs font-medium text-indigo-700" data-id="element-2261" data-mp="2ghu6heb6nofd">
                Characteristic avg
              </div>
              <div className="text-xl font-semibold text-slate-900" data-id="element-2262" data-mp="2v9kyl1scck8v">
                {avgCharacteristic.toFixed(1)}
              </div>
            </div>
            <div className="rounded-lg border border-teal-100 bg-teal-50 p-3" data-id="element-2263" data-mp="22i1cna4d0s36">
              <div className="text-xs font-medium text-teal-700" data-id="element-2264" data-mp="1xjzselv76mq4">
                Behaviour avg
              </div>
              <div className="text-xl font-semibold text-slate-900" data-id="element-2265" data-mp="2chabohe30wwe">
                {avgBehaviour.toFixed(1)}
              </div>
            </div>
          </div>
          <div className="h-[270px] w-full" data-id="element-2266" data-mp="27ka3o0xo9k2n">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2267" data-mp="3nv4f9yvpqbv8">
              <BarChart data={ROWS} layout="vertical" margin={{
              top: 0,
              right: 16,
              left: 0,
              bottom: 0
            }} data-id="element-2268" data-mp="3ix2sp3z2vuf0">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" data-id="element-2269" data-mp="1gc8jeeq5emz0" />
                <XAxis type="number" domain={[0, 5]} tick={{
                fill: '#94a3b8',
                fontSize: 11
              }} data-id="element-2270" data-mp="1l9s0dme8ddun" />
                <YAxis dataKey="short" type="category" width={140} tick={{
                fill: '#64748b',
                fontSize: 10
              }} data-id="element-2271" data-mp="1q70gzijqd9ts" />
                <Tooltip cursor={{
                fill: '#f8fafc'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }} data-id="element-2272" data-mp="1zxv56sgjejhv" />
                <Legend wrapperStyle={{
                fontSize: '12px'
              }} data-id="element-2273" data-mp="2904ifq36wqyy" />
                <Bar dataKey="characteristic" name="Characteristic" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={6} data-id="element-2274" data-mp="2dy5wfwlre03k" />
                <Bar dataKey="behaviour" name="Behaviour" fill="#14b8a6" radius={[0, 4, 4, 0]} barSize={6} data-id="element-2275" data-mp="ellc1cfj23bn" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-2276" data-mp="c0vuu1c7kcne">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-2277" data-mp="79dpl135jpwm">
          Rater Group Heat Map
        </h2>
        <p className="text-sm text-slate-500 mb-6" data-id="element-2278" data-mp="2a15up7kuyy9">
          Score by competency domain and rater group (5-point scale)
        </p>
        <div className="overflow-x-auto" data-id="element-2279" data-mp="3tiorz7dn6q6v">
          <table className="w-full min-w-[560px] border-separate border-spacing-1.5" data-id="element-2280" data-mp="uz82finh1egv">
            <thead data-id="element-2281" data-mp="ux5bmwehvhb3">
              <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-2282" data-mp="pzvgr18c6dfv">
                <th className="text-left font-semibold" data-id="element-2283" data-mp="l12235hbcge4">Domain</th>
                {['Self', 'Board', 'Peers', 'Direct reports'].map(h => <th key={h} className="text-center font-semibold w-24" data-id="element-2284" data-mp="g1pg0my9ndcl">
                    {h}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-2285" data-mp="30s6my0tcyeqt">
              {ROWS.map(r => <tr key={r.domain} data-id="element-2286" data-mp="30usuw1k5uw9k">
                  <td className="text-sm text-slate-700 pr-3" data-id="element-2287" data-mp="2azhsb4rdi2yp">{r.domain}</td>
                  {[r.self, r.board, r.peers, r.directs].map((v, i) => <td key={i} data-id="element-2288" data-mp="25z3jhkhy6ozm" data-mpi={i}>
                      <div className={`h-9 rounded-md flex items-center justify-center text-sm font-medium ${getHeatmapColor(v)}`} data-id="element-2289" data-mp="2kqkrg10qs7vc">
                        {v.toFixed(1)}
                      </div>
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500" data-id="element-2290" data-mp="2pri5q2imshrk">
          {[['bg-rose-500', 'Below 2.5'], ['bg-amber-300', '3.0 – 3.4'], ['bg-emerald-300', '3.5 – 3.9'], ['bg-emerald-500', '4.5 and above']].map(([c, l]) => <div key={l} className="flex items-center" data-id="element-2291" data-mp="14ccemy5rxcww">
              <div className={`w-3 h-3 rounded-sm ${c} mr-1.5`} data-id="element-2292" data-mp="14dxdgzz4fi0k" />
              {l}
            </div>)}
        </div>
      </section>
    </div>;
}
function StatCard({
  label,
  icon,
  children
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-2293" data-mp="367a97bb3uaes">
      <div className="flex items-center justify-between mb-2" data-id="element-2294" data-mp="3b6v7rkmd4gqz">
        <h3 className="text-sm font-medium text-slate-500" data-id="element-2295" data-mp="2x85b263ggj35">{label}</h3>
        {icon}
      </div>
      {children}
    </div>;
}